<template>
  <div
    class="mermaid-viewer"
    @click="onAction"
  >
    <!-- 渲染容器 -->
    <div
      v-show="!renderError"
      ref="container"
      class="mermaid-container"
    >
      <!-- 工具栏（hover 显示） -->
      <div class="mermaid-toolbar" role="toolbar" aria-label="Mermaid 图表工具栏">
        <button
          v-for="btn in toolbarButtons"
          :key="btn.action"
          type="button"
          class="mermaid-toolbar-button"
          :data-action="btn.action"
          :title="btn.label"
          :aria-label="btn.label"
          v-html="btn.icon"
        />
      </div>
      <div ref="canvas" class="mermaid-canvas" @mousedown="startDrag($event, 'canvas')" />
    </div>
    <!-- 渲染错误提示 -->
    <div v-if="renderError" class="mermaid-error">
      <span>图表渲染失败：{{ renderError }}</span>
    </div>
    <!-- 加载中 -->
    <div v-if="loading" class="mermaid-loading">
      <i class="el-icon-loading" />
      <span>渲染中...</span>
    </div>
    <!-- 源码弹窗 -->
    <el-dialog
      title="Mermaid 源码"
      :visible.sync="sourceVisible"
      width="720px"
      append-to-body
    >
      <pre class="mermaid-source-view">{{ sourceCode }}</pre>
      <span slot="footer" class="dialog-footer">
        <el-button @click="doCopySource">复制源码</el-button>
        <el-button type="primary" @click="sourceVisible = false">关闭</el-button>
      </span>
    </el-dialog>
    <!-- 全屏预览 -->
    <transition name="preview-fade">
      <div v-if="previewVisible" class="preview-overlay" @click.self="previewVisible = false">
        <i class="el-icon-close preview-close" @click="previewVisible = false"></i>
        <div class="preview-body">
          <div class="preview-svg-container" ref="previewContainer" @mousedown="startDrag($event, 'preview')"></div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
let mermaidPromise = null
let mermaidInitialized = false

function getMermaid() {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid/dist/mermaid.esm.min.mjs')
      .then((module) => {
        const instance = module.default || module
        if (!mermaidInitialized) {
          instance.initialize({
            startOnLoad: false,
            theme: 'default',
            securityLevel: 'strict',
            flowchart: { useMaxWidth: true, htmlLabels: true }
          })
          mermaidInitialized = true
        }
        return instance
      })
      .catch((error) => {
        console.error('[MermaidViewer] failed to load mermaid', error)
        return null
      })
  }
  return mermaidPromise
}

function normalizeMermaidCode(code) {
  return code.replace(/\r\n?/g, '\n').trim()
}

export default {
  name: 'MermaidViewer',
  props: {
    /** Mermaid 图表源码 */
    code: { type: String, required: true },
    /** 消息 ID（用于生成唯一 DOM id） */
    messageId: { type: [String, Number], default: '' }
  },
  data() {
    return {
      loading: false,
      renderError: '',
      scale: 1,
      sourceVisible: false,
      sourceCode: '',
      svgText: '',
      toolbarButtons: [
        { action: 'preview', icon: '<i class="el-icon-full-screen"></i>', label: '全屏预览' },
        { action: 'download', icon: '<i class="el-icon-download"></i>', label: '下载图片' },
        { action: 'zoom-in', icon: '<i class="el-icon-zoom-in"></i>', label: '放大' },
        { action: 'zoom-out', icon: '<i class="el-icon-zoom-out"></i>', label: '缩小' },
        { action: 'view-source', icon: '<i class="el-icon-view"></i>', label: '查看源码' }
      ],
      previewVisible: false,
      dragState: { active: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 }
    }
  },
  async mounted() {
    await this.renderDiagram()
  },
  beforeDestroy() {
    this.removeDragListeners()
  },
  methods: {
    /** 拖拽滚动 */
    startDrag(e, mode) {
      const el = mode === 'preview' ? this.$refs.previewContainer?.parentElement : this.$refs.canvas
      if (!el) return
      const ds = this.dragState
      ds.active = true
      ds.startX = e.clientX
      ds.startY = e.clientY
      ds.scrollLeft = el.scrollLeft
      ds.scrollTop = el.scrollTop
      el.style.cursor = 'grabbing'
      document.addEventListener('mousemove', this._onDragMove)
      document.addEventListener('mouseup', this._onDragEnd)
    },
    _onDragMove(e) {
      const ds = this.dragState
      if (!ds.active) return
      const el = document.querySelector('.mermaid-canvas') || this.$refs.previewContainer?.parentElement
      if (!el) return
      const dx = e.clientX - ds.startX
      const dy = e.clientY - ds.startY
      el.scrollLeft = ds.scrollLeft - dx
      el.scrollTop = ds.scrollTop - dy
    },
    _onDragEnd() {
      const ds = this.dragState
      ds.active = false
      document.querySelectorAll('.mermaid-canvas, .preview-body').forEach(el => { el.style.cursor = '' })
      document.removeEventListener('mousemove', this._onDragMove)
      document.removeEventListener('mouseup', this._onDragEnd)
    },
    removeDragListeners() {
      this._onDragEnd()
    },
    /** 渲染图表 — 对齐 mateClaw useMermaidRenderer：mermaid.parse 预校验 + 失败回退 raw source */
    async renderDiagram() {
      if (!this.code) return
      this.loading = true
      this.renderError = ''
      const mm = await getMermaid()
      if (!mm || typeof mm.render !== 'function') {
        this.renderError = 'Mermaid 库加载失败'
        this.loading = false
        return
      }
      const code = normalizeMermaidCode(this.code)

      // 先验证语法，失败就不调用 render（避免 mermaid 往 DOM 插临时 div）
      if (typeof mm.parse === 'function') {
        try {
          mm.parse(code)
        } catch (parseErr) {
          this.renderError = parseErr.str || parseErr.message || 'Mermaid 图表语法错误'
          this.loading = false
          return
        }
      }

      const id = `mv-${this.messageId || 'x'}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      try {
        const code = normalizeMermaidCode(this.code)

        // mermaid.parse 预校验 — 语法无效时直接展示 raw source
        const parsed = await mm.parse(code, { suppressErrors: true })
        if (!parsed) {
          this.renderError = this.code
          throw new Error('mermaid parse failed')
        }

        const id = `mv-${this.messageId || 'x'}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        const { svg } = await mm.render(id, code)

        const canvas = this.$refs.canvas
        if (!canvas) return
        canvas.innerHTML = svg
        this.svgText = svg
        this.sourceCode = this.code
      } catch (e) {
        if (!this.renderError) {
          this.renderError = e.message || '渲染异常'
        }
        console.warn('[MermaidViewer] render failed', e)
        const tempWrapper = document.getElementById('d' + id)
        if (tempWrapper) tempWrapper.remove()
      } finally {
        this.loading = false
      }
    },

    /** 获取 SVG 文本 */
    getSvgText() {
      const svg = this.$refs.canvas && this.$refs.canvas.querySelector('svg')
      if (!svg) return this.svgText || ''
      const clone = svg.cloneNode(true)
      if (!clone.getAttribute('xmlns')) clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
      if (!clone.getAttribute('xmlns:xlink')) clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink')
      return new XMLSerializer().serializeToString(clone)
    },

    /** 获取 SVG Blob */
    getSvgBlob() {
      const text = this.getSvgText()
      return text ? new Blob([text], { type: 'image/svg+xml;charset=utf-8' }) : null
    },

    /** 工具栏按钮点击 */
    onAction(e) {
      const btn = e.target && e.target.closest && e.target.closest('[data-action]')
      if (!btn) return
      const action = btn.getAttribute('data-action')
      if (action === 'download') this.download()
      else if (action === 'zoom-in') this.zoom(0.1)
      else if (action === 'zoom-out') this.zoom(-0.1)
      else if (action === 'view-source') this.viewSource()
      else if (action === 'preview') this.preview()
    },

    /** 下载 SVG */
    download() {
      const blob = this.getSvgBlob()
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `mermaid-${Date.now()}.svg`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      this.$message && this.$message.success('已下载 SVG 文件')
    },

    /** 缩放 */
    zoom(delta) {
      this.scale = Math.min(2.5, Math.max(0.5, this.scale + delta))
      const svg = this.$refs.canvas && this.$refs.canvas.querySelector('svg')
      if (svg) svg.style.transform = `scale(${this.scale})`
    },

    /** 查看源码 */
    viewSource() {
      this.sourceVisible = true
    },

    /** 全屏预览 */
    preview() {
      this.previewVisible = true
      this.$nextTick(() => {
        const previewEl = this.$refs.previewContainer
        if (!previewEl) return
        const srcSvg = this.$refs.canvas && this.$refs.canvas.querySelector('svg')
        if (srcSvg) {
          previewEl.innerHTML = ''
          const clone = srcSvg.cloneNode(true)
          clone.removeAttribute('width')
          clone.removeAttribute('height')
          clone.setAttribute('style', 'max-width:100%;max-height:100%;')
          previewEl.appendChild(clone)
        }
      })
    },

    /** 复制源码 */
    async doCopySource() {
      const text = this.sourceCode || this.code
      if (!text) return
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text)
          this.$message && this.$message.success('源码已复制')
          return
        }
        const ta = document.createElement('textarea')
        ta.value = text
        ta.setAttribute('readonly', '')
        ta.style.position = 'fixed'
        ta.style.left = '-9999px'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        this.$message && this.$message.success('源码已复制')
      } catch (_) {
        this.$message && this.$message.error('复制失败')
      }
    }
  }
}
</script>

<style scoped>
.mermaid-viewer {
  position: relative;
  width: 100%;
  margin: 12px 0;
}
.mermaid-container {
  position: relative;
  width: 100%;
  min-height: 40px;
  text-align: center;
  border-radius: 8px;
}
.mermaid-toolbar {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 5;
  display: flex;
  gap: 4px;
  padding: 4px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #ebeef5;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
.mermaid-container:hover .mermaid-toolbar {
  opacity: 1;
  pointer-events: auto;
}
.mermaid-toolbar-button {
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #606266;
  cursor: pointer;
  font-size: 14px;
}
.mermaid-toolbar-button:hover {
  color: #409eff;
  background: #ecf5ff;
}
.mermaid-canvas {
  width: 100%;
  overflow: auto;
  padding: 8px;
  box-sizing: border-box;
}
.mermaid-canvas :deep(svg) {
  display: inline-block;
  max-width: 100%;
  height: auto;
  transform-origin: center top;
  transition: transform 0.2s ease;
}
.mermaid-source-view {
  max-height: 60vh;
  overflow: auto;
  margin: 0;
  padding: 14px;
  color: #303133;
  background: #f5f7fa;
  border-radius: 6px;
  white-space: pre-wrap;
  word-break: break-word;
}
.mermaid-error {
  padding: 20px;
  color: #909399;
  text-align: left;
  white-space: pre-wrap;
  font-family: monospace;
  font-size: 13px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #eee;
}
.mermaid-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  color: #909399;
  font-size: 14px;
}
/* 全屏预览 */
.preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
}
.preview-close {
  position: absolute;
  top: 20px;
  right: 24px;
  font-size: 26px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  z-index: 10;
  transition: color 0.2s;
}
.preview-close:hover {
  color: #fff;
}
.preview-body {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 90%;
  height: 90%;
  overflow: auto;
}
.preview-svg-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: #f7f8fc;
}
.preview-svg-container :deep(svg) {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
}
.preview-fade-enter-active,
.preview-fade-leave-active {
  transition: opacity 0.2s ease;
}
.preview-fade-enter,
.preview-fade-leave-to {
  opacity: 0;
}

</style>
